import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import { faqData } from './FAQ';
import { FAQS as PRICING_FAQS } from './PricingPage';
import { FAQS as WEBAPPS_FAQS } from './WebAppsPage';
import { FAQS as PARTNER_FAQS } from './PartnersPage';
import { localPages } from './localPages';
import './PricingPage.css';
import './RateCard.css';

// Every question already answered across the site, on one indexable page.
// Each Q is a People-Also-Ask / AI-answer target; the schema carries all of them.

const GENERAL = faqData.map((f) => ({ q: f.question, a: f.answer }));

// One plain "what does it cost, and why" answer per service. Only numbers already
// published on indexed pages (/pricing, /web-apps). Ad-only prices on noindex pages
// (/lp/websites, /call/*, /tv, /plans) must not appear here.
const COST_FAQS = [
  {
    q: 'What does the AI phone receptionist cost, and why that price?',
    a: 'Plans start at $500 a month with 500 minutes included; Growth starts at $899 a month with 1,500 minutes plus CRM and calendar integrations, bilingual call flows and lead qualification; Custom starts at $1,799 a month for multi-location or complex intake. The price follows your call volume and how much the agent does on each call: just answering, or also booking, qualifying and writing notes into your tools. A one-time setup fee from $750 covers call-flow design, integrations and testing, and it is waived on a 12-month agreement. You can try it on your own calls free for two weeks before you commit.',
  },
  {
    q: 'What does a website from UBOTIKA cost, and why?',
    a: 'A website is quoted at a fixed one-time price before any work starts, so you approve the number up front. The price depends on how many pages you need, whether you also want a separate landing page for ads, and whether the site connects to a chat agent, booking calendar or AI receptionist. A small monthly fee covers hosting, fixes if anything breaks, and small updates. To get your number, request a free audit: we review your current website and phone flow by hand.',
  },
  {
    q: 'What does SEO and AI search visibility work cost, and why?',
    a: 'There is no single price, because the cost depends on what is actually keeping customers from finding you, such as technical problems, unclear service pages, or content that Google and AI assistants like ChatGPT cannot quote. We start with a free audit that reviews your website and shows where leads slip away. You then get a fixed scope and price for the fixes before any work starts.',
  },
  {
    q: 'What does a custom web app or tailor-made app cost, and why?',
    a: 'A single focused tool (one workflow, one team, secure sign-in, deployed) typically starts in the low five figures, and platforms with document AI, several integrations and dashboards scope higher. The cost is driven by how many workflows it covers and how many of your existing systems it has to connect to. You approve a fixed price per phase after a scoping call instead of paying by the hour, and you own the source code.',
  },
  {
    q: 'What does a live support agent for my website cost, and why?',
    a: 'A website agent that answers visitors’ questions and books while they are still deciding is included in our full-intake plan at $1,799 a month, which covers phone, email and web chat together. On its own, the price depends on how much of your site and services it has to know, whether it books appointments or sends leads into your CRM, and how many conversations it handles, so it is quoted after a free audit. You can try ours now: talk to Tessa in your browser from our homepage.',
  },
];

const SECTIONS = [
  {
    id: 'general',
    title: 'General',
    lede: 'The basics: what an AI receptionist is, how it sounds, and how it behaves on real calls.',
    faqs: GENERAL,
    source: null,
  },
  {
    id: 'costs',
    title: 'What it costs, and why',
    lede: 'Every service, what it costs and what drives the price. Where there is no fixed number, we explain what sets the price and how to get a quote.',
    faqs: COST_FAQS,
    source: { to: '/free-missed-lead-audit', label: 'Get a free audit' },
  },
  {
    id: 'pricing',
    title: 'Pricing & plans',
    lede: 'What it costs, what a plan includes, and how the free audit and two-week free trial work.',
    faqs: PRICING_FAQS,
    source: { to: '/pricing', label: 'See plans' },
  },
  {
    id: 'web-apps',
    title: 'Custom web apps & internal tools',
    lede: 'AI document review, SSO portals, intake workflows, and dashboards we build and run.',
    faqs: WEBAPPS_FAQS,
    source: { to: '/web-apps', label: 'See what we build' },
  },
  {
    id: 'partners',
    title: 'Partner program',
    lede: 'Referral, reseller, and white-label terms for agencies and consultants.',
    faqs: PARTNER_FAQS,
    source: { to: '/partners', label: 'Partner program' },
  },
];

const INDUSTRY_PAGES = localPages.filter((p) => p.faqs && p.faqs.length);

const ALL_FAQS = [
  ...SECTIONS.flatMap((s) => s.faqs),
  ...INDUSTRY_PAGES.flatMap((p) => p.faqs),
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ALL_FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${SITE_URL}/faq` },
    ],
  },
];

function FaqHubPage() {
  return (
    <>
      <SEO
        title={`AI Receptionist FAQ — ${ALL_FAQS.length} Questions Answered | UBOTIKA`}
        description="Every question we get about AI receptionists, answered straight: pricing, setup time, bilingual coverage, integrations, custom web apps, and industry-specific concerns for contractors, dental, legal, salons, restaurants, and property management."
        path="/faq"
        schema={schema}
      />
      <main id="main-content" className="pricing-page ratecard-page">
        <section className="pricing-hero">
          <h1>{ALL_FAQS.length} questions, answered straight.</h1>
          <p className="pricing-sub">
            Everything we get asked about AI receptionists, custom web apps, pricing, and
            the partner program — collected from every page of this site. Prefer to just
            hear it? Call <a href="tel:+14244344541">(424) 434-4541</a> — an AI answers.
          </p>
        </section>

        <section className="ratecard-section">
          <nav className="local-related-links" aria-label="FAQ sections">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`}>{s.title}</a>
            ))}
            <a href="#industries">By industry</a>
          </nav>
        </section>

        {SECTIONS.map((s) => (
          <section className="ratecard-section" id={s.id} key={s.id}>
            <h2>{s.title}</h2>
            <p className="ratecard-lede">
              {s.lede}
              {s.source && (
                <>
                  {' '}
                  <Link to={s.source.to}>{s.source.label} →</Link>
                </>
              )}
            </p>
            <div className="pricing-faq-list">
              {s.faqs.map((f) => (
                <article className="pricing-faq-item" key={f.q}>
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </article>
              ))}
            </div>
          </section>
        ))}

        <section className="ratecard-section" id="industries">
          <h2>By industry</h2>
          <p className="ratecard-lede">
            The questions each industry actually asks — from the teams answering their
            phones today.
          </p>
          {INDUSTRY_PAGES.map((p) => (
            <div key={p.slug} className="faqhub-industry">
              <h3 className="faqhub-industry-title">
                {p.shortTitle}{' '}
                <Link to={`/${p.slug}`} className="faqhub-industry-link">
                  full page →
                </Link>
              </h3>
              <div className="pricing-faq-list">
                {p.faqs.map((f) => (
                  <article className="pricing-faq-item" key={f.q}>
                    <h3>{f.q}</h3>
                    <p>{f.a}</p>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="ratecard-pilot">
          <h2>Question not here?</h2>
          <p>
            Call <a href="tel:+14244344541">(424) 434-4541</a> and ask the AI itself — it
            answers the way it would answer your customers. Or start with a free audit and
            we&apos;ll answer it against your actual call volume.
          </p>
          <Link to="/free-missed-lead-audit" className="cta-button">Get a free audit</Link>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default FaqHubPage;
